import type {PrismicRichTextProps} from '@prismicio/react'
import React from 'react'
import styled, {css} from 'styled-components'

import Button from '@/components/button'
import LocalizedLink from '@/components/localized-link'
import PrismicLink from '@/components/prismic-link'
import RichText, {hasRichText} from '@/components/rich-text'
import {Video} from '@/components/video'
import type {PrismicLinkFragment} from '@/graphql-types'
import {useLocale} from '@/providers/locale'
import {useSignupModal} from '@/providers/signup-modal'
import {Section} from '@/styles/grid'
import type {ImageSource} from '@/types/image'
import {getVideoSources} from '@/utils/get-video-sources'
import type {Video as VideoProps} from '@/utils/get-video-sources'
import mq from '@/utils/media-query'

export type Props = {
    title: string
    submitText: string
    body: string | PrismicRichTextProps['field']
    ctaText: string
    ctaLink: PrismicLinkFragment
    videoWebM: VideoProps
    videoMP4: VideoProps
    videoPosterImage: ImageSource
    isFullWidth?: boolean
    isSquareOnMobile?: boolean
}

const VIDEO_MAX_WIDTH = 1440

export const VideoHero = React.forwardRef<HTMLDivElement, Props>(
    (
        {
            title,
            submitText,
            body,
            ctaText,
            ctaLink,
            videoWebM,
            videoMP4,
            videoPosterImage,
            isFullWidth = false,
            isSquareOnMobile = false,
        }: Props,
        ref,
    ) => {
        const {renderSignupModal} = useSignupModal()
        const currentLocale = useLocale()

        const video = getVideoSources({
            webm: videoWebM,
            mp4: videoMP4,
        })

        const openSignupModal = () => {
            renderSignupModal()
        }

        const getCTAButton = (label: string) => {
            if (currentLocale.code === 'en-us') {
                return (
                    <StyledButton forwardedAs={LocalizedLink} to="/getting-started">
                        {label}
                    </StyledButton>
                )
            }

            return (
                <StyledButton data-testid="homepage-hero-signup-cta" onClick={openSignupModal}>
                    {label}
                </StyledButton>
            )
        }

        return (
            <Section>
                <div ref={ref}>
                    {(hasRichText(title) || hasRichText(body) || !!ctaText || !!submitText) && (
                        <Content>
                            {hasRichText(title) && (
                                <RichText
                                    forwardedAs={Title}
                                    $variant="heading-large"
                                    content={title}
                                />
                            )}
                            {hasRichText(body) && (
                                <LeadText
                                    $color="muted"
                                    $variant="paragraph-large"
                                    content={body}
                                />
                            )}
                            {(ctaText || submitText) && (
                                <ButtonWrapper>
                                    {submitText && getCTAButton(submitText)}
                                    {ctaText && (
                                        <StyledButton
                                            forwardedAs={PrismicLink}
                                            {...ctaLink}
                                            $variant="outline"
                                        >
                                            {ctaText}
                                        </StyledButton>
                                    )}
                                </ButtonWrapper>
                            )}
                        </Content>
                    )}

                    {video && (
                        <StyledVideo
                            video={video}
                            poster={videoPosterImage.url}
                            dimensions={videoPosterImage.dimensions}
                            posterWidth={VIDEO_MAX_WIDTH}
                            posterLoading="eager"
                            $isFullWidth={isFullWidth}
                            $isSquareOnMobile={isSquareOnMobile}
                            triggerThreshold={0.75}
                        />
                    )}
                </div>
            </Section>
        )
    },
)

const Content = styled.div`
    margin: 0 auto;

    ${mq.max('sm')} {
        padding-bottom: ${({theme}) => theme.spacings.s40};
    }
`

const Title = styled.h1`
    ${mq.min('sm')} {
        text-align: center;
    }
`

const LeadText = styled(RichText)`
    margin: ${({theme}) => theme.spacings.s12} auto 0;
    max-width: 840px;

    ${mq.min('sm')} {
        margin-top: ${({theme}) => theme.spacings.s20};
        text-align: center;
    }
`

const ButtonWrapper = styled.div`
    display: flex;
    justify-content: center;
    flex-wrap: wrap;
    gap: ${({theme}) => theme.spacings.s12};
    padding: ${({theme}) => theme.spacings.s20} 0;

    ${mq.min('sm')} {
        padding: ${({theme}) => theme.spacings.s40} 0;
        gap: ${({theme}) => theme.spacings.s20};
        flex-wrap: nowrap;
    }

    ${mq.min('md')} {
        padding-bottom: 80px;
    }
`

const StyledButton = styled(Button)`
    ${mq.max('sm')} {
        width: 100%;
    }
`

const squareRatioStyles = css`
    ${mq.max('sm')} {
        aspect-ratio: 1 / 1;
        object-fit: cover;
    }
`

const StyledVideo = styled(Video)<{$isFullWidth: boolean; $isSquareOnMobile: boolean}>`
    margin: ${({theme}) => theme.spacings.s20} auto 0;
    max-width: ${(props) => (props.$isFullWidth ? '100%' : '1040px')};

    ${(props) => props.$isSquareOnMobile && squareRatioStyles}

    ${mq.min('sm')} {
        margin-top: ${({theme}) => theme.spacings.s40};
    }
`
